import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { useSEO } from "@/lib/seo";
import { fetchWithRetry, isNotFound } from "@/lib/fetchRetry";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Legacy WooCommerce product-category slug -> current destination.
// Covers the top segments found in the Wayback CDX crawl of the old site
// (jewelry, rings, logoart, pendants-and-charms, accessories, …) plus nested
// second-level paths for precision. Unmapped/null slugs retire cleanly.
const CATEGORY_MAP = {
  "chains": "/catalog?category=Chains",
  "gold-chains": "/catalog?category=Chains",
  "necklaces": "/catalog?category=Necklaces",
  "pendants": "/catalog?category=Pendants & Charms",
  "pendants-and-charms": "/catalog?category=Pendants & Charms",
  "charms": "/catalog?category=Pendants & Charms",
  "earrings": "/catalog?category=Earrings",
  "hoop-earrings": "/collections/hoop-earrings",
  "stud-earrings": "/collections/stud-earrings",
  "rings": "/catalog?category=Rings",
  "mens-rings": "/collections/mens-rings",
  "wedding-bands": "/collections/wedding-bands",
  "wedding-rings": "/collections/wedding-bands",
  "engagement-rings": "/catalog?category=Rings",
  "engagement-wedding": "/catalog?category=Rings",
  "bracelets": "/catalog?category=Bracelets",
  "anklets": "/catalog?category=Anklets",
  "cuban-link-chains": "/collections/cuban-link-chains",
  "rope-chains": "/collections/rope-chains",
  "figaro-chains": "/collections/figaro-chains",
  "watches": "/collections/watches",
  "nameplates": "/collections/nameplates",
  "name-plates": "/collections/nameplates",
  "lab-grown-diamonds": "/collections/lab-grown-diamonds",
  "loose-diamonds": "/collections/loose-stones",
  "loose-stones": "/collections/loose-stones",
  "gifts": "/collections/jewelry-under-500",
  "gift": "/collections/jewelry-under-500",
  "mens-jewelry": "/catalog?category=Jewelry",
  "jewelry": "/catalog",
  "jewelry/necklaces": "/catalog?category=Necklaces",
  "jewelry/bracelets": "/catalog?category=Bracelets",
  "jewelry/chains": "/catalog?category=Chains",
  "jewelry/earrings": "/catalog?category=Earrings",
  "jewelry/anklets": "/catalog?category=Anklets",
  "jewelry/beads": "/catalog?category=Beads",
  "jewelry/rings": "/catalog?category=Rings",
  "jewelry/sets-jewelry": "/catalog?category=Sets",
  "jewelry/mountings": "/catalog?category=Mountings",
  "accessories": "/catalog?category=Accessories",
  "accessories/key-rings": "/catalog?category=Key Rings",
  "accessories/money-clips": "/catalog?category=Money Clips",
  "accessories/cuff-links-collar-stays": "/catalog?category=Cuff Links",
  "collections": "/collections",
  "logoart": "/catalog?q=logoart",
  "sale-clearance": "/catalog",
  // Genuinely dead, no shopper equivalent:
  "packaging": null,
  "x": null,
};

function SeoShell({ title, children, testid }) {
  return (
    <div className="min-h-screen bg-[#F5F3EC]">
      <Header />
      <main className="pt-[164px] lg:pt-[196px] pb-24 px-6" data-testid={testid}>
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-3">Gold Time Jewelry</p>
          <h1 className="font-serif text-3xl lg:text-4xl text-neutral-900 mb-4">{title}</h1>
          {children}
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            <Link to="/catalog" className="rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3" data-testid={`${testid}-catalog`}>
              Browse the Catalog
            </Link>
            <Link to="/collections" className="rounded-full border border-[#C9A227] text-[#A8871B] text-sm font-semibold px-6 py-3" data-testid={`${testid}-collections`}>
              Shop Collections
            </Link>
            <Link to="/contact" className="rounded-full border border-[#E8DCC4] text-neutral-600 text-sm px-6 py-3" data-testid={`${testid}-contact`}>
              Contact Us
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function NotFound() {
  const { pathname } = useLocation();
  useSEO({ title: "Page Not Found | Gold Time Jewelry", canonicalPath: pathname, noindex: true });
  return (
    <SeoShell title="We couldn't find that page" testid="page-404">
      <p className="text-sm text-neutral-600 leading-relaxed">
        The page you're looking for doesn't exist or may have moved. Our full collection is always available below.
      </p>
    </SeoShell>
  );
}

export function Gone() {
  const { pathname } = useLocation();
  useSEO({ title: "This Page Has Been Retired | Gold Time Jewelry", canonicalPath: pathname, noindex: true });
  return (
    <SeoShell title="This page has been retired" testid="page-410">
      <p className="text-sm text-neutral-600 leading-relaxed">
        This is a link from our previous website and is no longer available. Everything we carry now lives in the new catalog — including tens of thousands of fine jewelry styles.
      </p>
    </SeoShell>
  );
}

// /product/<slug>/ — best-effort 301-equivalent to the matching live product,
// otherwise a clean retirement page (never a silent homepage bounce).
export function LegacyProduct() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState("resolving");
  useSEO({ title: "Finding your piece… | Gold Time Jewelry", canonicalPath: pathname, noindex: true });

  useEffect(() => {
    const slug = pathname.replace(/^\/product\//, "").replace(/\/+$/, "").split("/")[0];
    if (!slug) {
      navigate("/catalog", { replace: true });
      return;
    }
    let cancelled = false;
    setState("resolving");
    // Transient resolver failures (timeout/5xx/429) retry, then show a
    // recoverable error — a hiccup must NEVER render the "retired" page for a
    // URL that may have a live match (2026-10-11 noindex incident rule).
    fetchWithRetry(`${API}/legacy/resolve-product`, { params: { slug } })
      .then(({ data }) => {
        if (cancelled) return;
        if (data.path) {
          navigate(data.path, { replace: true });
        } else {
          setState("gone");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState(isNotFound(err) ? "gone" : "error");
      });
    return () => { cancelled = true; };
  }, [pathname, navigate]);

  if (state === "gone") return <Gone />;
  if (state === "error") {
    return (
      <SeoShell title="Having trouble reaching the catalog" testid="legacy-error">
        <p className="text-sm text-neutral-600 leading-relaxed">
          A temporary problem on our side — not a missing page. Please try again in a moment.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-6 py-3"
          data-testid="legacy-retry-button"
        >
          Try again
        </button>
      </SeoShell>
    );
  }
  return (
    <SeoShell title="Finding your piece…" testid="legacy-resolving">
      <p className="text-sm text-neutral-500">One moment — taking you to the current page.</p>
    </SeoShell>
  );
}

// /product-category/<slug>/ (and /page/N/ pagination under it) — mapped to the
// closest current collection/catalog view, or retired.
export function LegacyCategory() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  useSEO({ title: "Collection moved | Gold Time Jewelry", canonicalPath: pathname, noindex: true });

  const parts = pathname
    .replace(/^\/product-category\//, "")
    .replace(/\/+$/, "")
    .split("/page/")[0]
    .split("/")
    .filter(Boolean);
  // Most specific first: two-segment path (jewelry/chains) then top segment.
  const two = parts.slice(0, 2).join("/");
  const target = CATEGORY_MAP[two] !== undefined ? CATEGORY_MAP[two] : CATEGORY_MAP[parts[0]];

  useEffect(() => {
    if (target) navigate(target, { replace: true });
  }, [target, navigate]);

  if (!target) return <Gone />;
  return (
    <SeoShell title="Taking you to the collection…" testid="legacy-category-redirect">
      <p className="text-sm text-neutral-500">One moment — that collection has a new home.</p>
    </SeoShell>
  );
}

// Simple mapped redirects for legacy static paths (/shop, /my-account, …).
export function LegacyStaticRedirect({ to, label }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useSEO({ title: `${label} — moved | Gold Time Jewelry`, canonicalPath: pathname, noindex: true });
  useEffect(() => {
    navigate(to, { replace: true });
  }, [to, navigate]);
  return (
    <SeoShell title="This page has moved" testid="legacy-static-redirect">
      <p className="text-sm text-neutral-500">One moment — taking you there now.</p>
    </SeoShell>
  );
}
